import { useMemo } from 'react';
import { Activity } from 'lucide-react';
import { useLiveStream } from '@/lib/ws';
import { cn } from '@/lib/utils';
import { Tooltip } from './Tooltip';

interface StatsPayload {
  type: 'stats';
  username: string | null;
  guildCount: number;
  memberCount: number;
  ping: number;
  uptimeMs: number;
  timestamp: string;
}

/**
 * Bot status for the app rail: an icon with a status dot — green if we've
 * seen a `stats` event from the live stream in the last 60s (bot alive and
 * publishing), yellow if only the WebSocket is up, red if disconnected.
 * Name, servers, ping and uptime show in the tooltip.
 */
export function BotStatusIndicator() {
  const { messages, connected } = useLiveStream(200);

  const latest = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      const p = m.payload as { type?: string } | undefined;
      if (m.channel === 'bot:event' && p?.type === 'stats') {
        return m.payload as StatsPayload;
      }
    }
    return null;
  }, [messages]);

  const isFresh = latest && Date.now() - new Date(latest.timestamp).getTime() < 60_000;
  const state = connected && isFresh ? 'online' : connected ? 'stale' : 'offline';

  const label = (
    <div className="space-y-0.5 py-0.5">
      <div className="font-semibold">
        {state === 'online'
          ? `${latest?.username ?? 'Bot'} · online`
          : state === 'stale'
            ? 'Verbunden, keine Stats seit 60 s'
            : 'Live-Verbindung getrennt'}
      </div>
      {latest && (
        <div className="font-normal text-muted-foreground">
          {latest.guildCount} Server · {latest.memberCount.toLocaleString('de-DE')} Members · Ping {latest.ping} ms ·
          Uptime {formatUptime(latest.uptimeMs)}
        </div>
      )}
    </div>
  );

  return (
    <Tooltip label={label}>
      <span
        className="relative flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground"
        aria-label="Bot-Status"
      >
        <Activity className="h-5 w-5" />
        <span
          className={cn(
            'absolute bottom-1.5 right-1.5 h-2.5 w-2.5 rounded-full ring-2 ring-card',
            state === 'online' ? 'bg-emerald-500' : state === 'stale' ? 'bg-yellow-500' : 'bg-destructive'
          )}
        />
      </span>
    </Tooltip>
  );
}

function formatUptime(ms: number): string {
  const secs = Math.floor(ms / 1000);
  const days = Math.floor(secs / 86400);
  const hours = Math.floor((secs % 86400) / 3600);
  const mins = Math.floor((secs % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}
